'use client';

import { useCallback, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Maximize2, Minimize2 } from 'lucide-react';
import { ThreadBadgeBar } from './ThreadBadgeBar';
import { ChatHeader } from './ChatHeader';
import { MessageScrollArea } from './MessageScrollArea';
import { ChatInput } from './ChatInput';
import { buildThreadsFromCorrespondence } from '../services/thread.service';
import type {
  CorrespondenceChatProps,
  ThreadVisibility,
  ChatThread,
  ChatMessage,
  InstitutionCorrespondence
} from '../types';

// Mock data import - will be replaced with API data
import { mockCorrespondenceScenarios } from '../mocks/correspondence-data';

export function CorrespondenceChatV2({
  matterId,
  institutionId,
  institutionName,
  matterReference,
  deceasedName,
  fullHeight = false,
  showThreadList: _showThreadList = false,
  externalMessages,
  onSendMessage: onSendMessageProp
}: CorrespondenceChatProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [userThreadVisibility, setUserThreadVisibility] =
    useState<ThreadVisibility>({});
  const [localMessages, setLocalMessages] = useState<
    InstitutionCorrespondence[]
  >([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [userMessageSubject, setUserMessageSubject] = useState<string | null>(
    null
  );

  // Get mock data based on matterId (will be replaced with API call)
  const baseMockCorrespondence =
    externalMessages ||
    (matterId === 99
      ? mockCorrespondenceScenarios.empty
      : matterId === 180
        ? mockCorrespondenceScenarios.withDrafts.filter((c) =>
            institutionId ? c.institutionId === institutionId : true
          )
        : mockCorrespondenceScenarios.active.filter((c) =>
            institutionId ? c.institutionId === institutionId : true
          ));

  // Combine base mock data with local messages
  const mockCorrespondence = useMemo(
    () => [...baseMockCorrespondence, ...localMessages],
    [baseMockCorrespondence, localMessages]
  );

  // Build threads from correspondence
  const threads = useMemo(() => {
    return buildThreadsFromCorrespondence(mockCorrespondence, institutionName);
  }, [mockCorrespondence, institutionName]);

  // Derived thread visibility: union of user toggles + default false for any
  // thread the user hasn't interacted with yet. No effect needed.
  const threadVisibility = useMemo<ThreadVisibility>(() => {
    const result: ThreadVisibility = {};
    threads.forEach((thread) => {
      result[thread.id] = userThreadVisibility[thread.id] ?? false;
    });
    return result;
  }, [threads, userThreadVisibility]);
  const setThreadVisibility = useCallback(
    (
      update: ThreadVisibility | ((prev: ThreadVisibility) => ThreadVisibility)
    ) => {
      setUserThreadVisibility((prev) =>
        typeof update === 'function' ? update(prev) : update
      );
    },
    []
  );

  // Default message subject derived from the first thread until the user edits.
  const derivedMessageSubject = useMemo(() => {
    if (threads.length === 0) return '';
    const firstThread = threads[0];
    return firstThread.subject.startsWith('RE:')
      ? firstThread.subject
      : `RE: ${firstThread.subject}`;
  }, [threads]);
  const messageSubject = userMessageSubject ?? derivedMessageSubject;
  const setMessageSubject = useCallback(
    (subject: string) => setUserMessageSubject(subject),
    []
  );

  // Build unified view with filtering
  const { visibleMessages, threadMap } = useMemo(() => {
    // Create a map of thread IDs to thread info
    const threadMap = new Map<string, ChatThread>();
    threads.forEach((thread) => {
      threadMap.set(thread.id, thread);
    });

    // Get all messages from all threads
    const allMessages: Array<{ message: ChatMessage; thread: ChatThread }> = [];
    threads.forEach((thread) => {
      thread.messages.forEach((message) => {
        allMessages.push({ message, thread });
      });
    });

    // Filter out drafts from display and sort chronologically
    const nonDraftMessages = allMessages.filter(
      (item) => !item.message.isDraft && item.message.status !== 'draft'
    );

    // Sort non-draft messages chronologically
    const sortedMessages = nonDraftMessages.sort(
      (a, b) => a.message.timestamp.getTime() - b.message.timestamp.getTime()
    );

    // Check if any threads are selected
    const hasSelectedThreads = Object.values(threadVisibility).some(
      (v) => v === true
    );

    // Filter messages - if no threads selected, show all. Otherwise show only selected threads
    const visibleMessages = sortedMessages.filter(
      (item) => !hasSelectedThreads || threadVisibility[item.thread.id] === true
    );

    return { visibleMessages, threadMap };
  }, [threads, threadVisibility]);

  // Create a unified thread for display
  const unifiedThread = useMemo(() => {
    if (visibleMessages.length === 0) return null;

    const messages = visibleMessages.map((item) => item.message);
    const lastMessage = messages[messages.length - 1];
    const unreadCount = messages.filter(
      (m) => !m.isRead && m.direction === 'inbound'
    ).length;

    return {
      id: 'unified-thread',
      subject: `Correspondence with ${institutionName}`,
      institution: {
        id: institutionId || 1,
        name: institutionName
      },
      lastMessage: {
        content: lastMessage.content,
        timestamp: lastMessage.timestamp,
        sender: lastMessage.sender,
        direction: lastMessage.direction
      },
      messages,
      participants: [],
      unreadCount,
      status: 'received' as const,
      hasAttachments: messages.some(
        (m) => m.attachments && m.attachments.length > 0
      ),
      hasDraft: messages.some((m) => m.status === 'draft'),
      needsFollowUp: false,
      type: 'notification' as const
    };
  }, [visibleMessages, institutionName, institutionId]);

  // Handlers
  const handleToggleThread = (threadId: string) => {
    setThreadVisibility((prev) => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };

  const handleToggleAll = () => {
    // Always reset to show all threads
    const newVisibility: ThreadVisibility = {};
    threads.forEach((thread) => {
      newVisibility[thread.id] = false;
    });
    setThreadVisibility(newVisibility);
  };

  const handleExclusiveThread = (threadId: string) => {
    const newVisibility: ThreadVisibility = {};
    threads.forEach((thread) => {
      newVisibility[thread.id] = thread.id === threadId;
    });
    setThreadVisibility(newVisibility);
  };

  const handleSendMessage = (content: string) => {
    // If external handler provided, use it with subject
    if (onSendMessageProp) {
      const finalSubject = messageSubject || 'New message';
      onSendMessageProp(content, finalSubject);
      return;
    }

    // Remove any existing drafts since we're sending a message
    setLocalMessages((prev) => prev.filter((msg) => msg.status !== 'draft'));

    // Otherwise, handle locally
    // Use the subject from the subject field or default
    const finalSubject = messageSubject || 'New message';

    const newMessage: InstitutionCorrespondence = {
      id: `local-${Date.now()}`,
      institutionId: institutionId || 1,
      matterId: matterId,
      subject: finalSubject,
      content: content,
      type: 'notification' as const,
      direction: 'outbound' as const,
      status: 'sent' as const,
      sentAt: new Date().toISOString(),
      attachments: [],
      sender: 'You',
      recipient: institutionName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Add to local messages
    setLocalMessages((prev) => [...prev, newMessage]);

    // TODO: In production, this would call an API to persist the message
    console.log('Message sent:', content);
  };

  const handleSaveDraft = (
    content: string,
    existingDraftId?: string
  ): string => {
    let draftId: string;

    if (existingDraftId) {
      // Update existing draft
      setLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === existingDraftId
            ? { ...msg, content, updatedAt: new Date().toISOString() }
            : msg
        )
      );
      draftId = existingDraftId;
    } else {
      // Create new draft (remove any existing drafts first)
      setLocalMessages((prev) => prev.filter((msg) => msg.status !== 'draft'));

      draftId = `draft-${Date.now()}`;
      // Use the subject from the subject field
      const draftSubject = messageSubject || 'New message';

      const draftMessage: InstitutionCorrespondence = {
        id: draftId,
        institutionId: institutionId || 1,
        matterId: matterId,
        subject: draftSubject,
        content: content,
        type: 'notification' as const,
        direction: 'outbound' as const,
        status: 'draft' as const,
        sentAt: new Date().toISOString(),
        attachments: [],
        sender: 'John Smith (Vale Legal)',
        recipient: institutionName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to local messages
      setLocalMessages((prev) => [...prev, draftMessage]);
    }

    // TODO: In production, this would call an API to save the draft
    console.log('Draft saved:', content, draftId);
    return draftId;
  };

  const handleAttachment = () => {
    // TODO: Implement file upload
    console.log('Opening file attachment dialog');
  };

  const handleSendDraft = (messageId: string) => {
    // Find the draft message
    const draftMessage = mockCorrespondence.find((msg) => msg.id === messageId);
    if (!draftMessage) return;

    // Create a new sent message based on the draft
    const sentMessage: InstitutionCorrespondence = {
      ...draftMessage,
      status: 'sent' as const,
      sentAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Update local messages by replacing the draft with sent version
    setLocalMessages((prev) => {
      const filtered = prev.filter((msg) => msg.id !== messageId);
      return [...filtered, sentMessage];
    });

    // Clear the editing state if this was the draft being edited
    if (editingDraftId === messageId) {
      setEditingDraftId(null);
    }

    // TODO: In production, this would call an API to update the message status
    console.log('Draft message sent:', messageId);
  };

  const handleDeleteDraft = (messageId: string) => {
    // Remove the draft message from local messages
    setLocalMessages((prev) => prev.filter((msg) => msg.id !== messageId));

    // TODO: In production, this would:
    // 1. Show a confirmation dialog first
    // 2. Call API to delete the draft
    // 3. Show a success notification
    console.log('Draft message deleted:', messageId);
  };

  const handleEditDraft = (messageId: string) => {
    if (editingDraftId === messageId) {
      // Cancel editing
      setEditingDraftId(null);
    } else {
      // Start editing
      setEditingDraftId(messageId);
    }
  };

  // Get the content of the draft being edited
  const getEditingDraftContent = () => {
    if (!editingDraftId) return '';
    const draft = mockCorrespondence.find((msg) => msg.id === editingDraftId);
    return draft?.content || '';
  };

  // Handle updating a draft
  const handleUpdateDraft = (content: string) => {
    if (!editingDraftId) return;

    // Update the draft in local messages
    setLocalMessages((prev) =>
      prev.map((msg) =>
        msg.id === editingDraftId
          ? { ...msg, content, updatedAt: new Date().toISOString() }
          : msg
      )
    );

    // Clear editing state
    setEditingDraftId(null);

    console.log('Draft updated:', editingDraftId);
  };

  const handleDownload = () => {
    // Generate correspondence report
    const hasSelectedThreads = Object.values(threadVisibility).some(
      (v) => v === true
    );
    const messagesToExport = visibleMessages;

    // Create text content
    let content = `Correspondence Report\n`;
    content += `Matter Reference: ${matterReference || 'N/A'}\n`;
    content += `Deceased: ${deceasedName || 'N/A'}\n`;
    content += `Institution: ${institutionName}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `\n${'='.repeat(60)}\n\n`;

    if (hasSelectedThreads) {
      const selectedThreadNames = threads
        .filter((t) => threadVisibility[t.id] === true)
        .map((t) => t.subject)
        .join(', ');
      content += `Filtered Threads: ${selectedThreadNames}\n`;
      content += `${'='.repeat(60)}\n\n`;
    }

    // Group messages by date
    let currentDate = '';
    messagesToExport.forEach(({ message, thread }) => {
      const messageDate = message.timestamp.toLocaleDateString();

      if (messageDate !== currentDate) {
        content += `\n--- ${messageDate} ---\n\n`;
        currentDate = messageDate;
      }

      const time = message.timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      const sender = message.direction === 'outbound' ? 'You' : message.sender;
      const isDraft = message.isDraft ? ' [DRAFT]' : '';

      content += `[${time}] ${sender}${isDraft}`;
      if (thread.subject !== 'Correspondence with ' + institutionName) {
        content += ` (${thread.subject})`;
      }
      content += `\n${message.content}\n`;

      if (message.attachments && message.attachments.length > 0) {
        content += `Attachments: ${message.attachments.map((a) => a.filename).join(', ')}\n`;
      }

      content += '\n';
    });

    // Create and download file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `correspondence-${matterReference || 'export'}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderChat = (extraClassName?: string) => (
    <div
      className={cn(
        'bg-card flex flex-col rounded-lg',
        fullHeight ? 'h-screen max-h-screen' : 'h-[600px]',
        extraClassName
      )}
    >
      {/* Thread Badge Bar - Fixed at top */}
      <div className="z-10 shrink-0">
        <ThreadBadgeBar
          threads={threads}
          visibility={threadVisibility}
          onToggleThread={handleToggleThread}
          onToggleAll={handleToggleAll}
          onExclusiveThread={handleExclusiveThread}
        />
      </div>

      {/* Main content area with flex to push input to bottom */}
      {unifiedThread && (
        <div className="flex min-h-0 flex-1 flex-col">
          {fullHeight && (
            <>
              {/* Header */}
              <div className="shrink-0">
                <ChatHeader
                  institutionName={institutionName}
                  unreadCount={unifiedThread.unreadCount}
                  onDownload={handleDownload}
                />
              </div>

              {/* Messages - Scrollable area that takes remaining space */}
              <div className="min-h-0 flex-1 overflow-hidden">
                <MessageScrollArea
                  thread={unifiedThread}
                  visibleMessages={visibleMessages}
                  threadMap={threadMap}
                  onSendDraft={handleSendDraft}
                  onDeleteDraft={handleDeleteDraft}
                  onEditDraft={handleEditDraft}
                  editingDraftId={editingDraftId}
                />
              </div>

              {/* Chat Input with Subject - Always at bottom */}
              <div className="mt-auto shrink-0">
                <ChatInput
                  onSend={handleSendMessage}
                  onSaveDraft={handleSaveDraft}
                  onAttach={handleAttachment}
                  placeholder={`Message ${institutionName}...`}
                  editingDraftContent={getEditingDraftContent()}
                  onUpdateDraft={handleUpdateDraft}
                  onCancelEdit={() => setEditingDraftId(null)}
                  subject={messageSubject}
                  onSubjectChange={setMessageSubject}
                />
              </div>
            </>
          )}

          {/* Non-fullHeight view */}
          {!fullHeight && (
            <EnhancedMessageView
              thread={unifiedThread}
              visibleMessages={visibleMessages}
              threadMap={threadMap}
              onSendMessage={handleSendMessage}
              onAttachment={handleAttachment}
              onDownload={handleDownload}
              onSendDraft={handleSendDraft}
              onDeleteDraft={handleDeleteDraft}
              institutionName={institutionName}
              fullHeight={false}
              editingDraftId={editingDraftId}
              getEditingDraftContent={getEditingDraftContent}
              handleUpdateDraft={handleUpdateDraft}
              setEditingDraftId={setEditingDraftId}
              handleSaveDraft={handleSaveDraft}
              onEditDraft={handleEditDraft}
              messageSubject={messageSubject}
              setMessageSubject={setMessageSubject}
            />
          )}
        </div>
      )}
    </div>
  );

  if (isFullScreen) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsFullScreen(true)}
          className="absolute -top-10 right-0"
        >
          <Maximize2 className="h-4 w-4" />
          <span className="sr-only">Fullscreen</span>
        </Button>

        <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
          <DialogContent className="flex h-[90vh] w-full max-w-[95vw] flex-col p-0">
            <DialogHeader className="shrink-0 border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <DialogTitle>Correspondence with {institutionName}</DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullScreen(false)}
                >
                  <Minimize2 className="h-4 w-4" />
                  <span className="sr-only">Exit fullscreen</span>
                </Button>
              </div>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-hidden">
              {renderChat('rounded-none border-0 h-full')}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsFullScreen(true)}
        className="absolute -top-10 right-0 z-10"
      >
        <Maximize2 className="h-4 w-4" />
        <span className="sr-only">Fullscreen</span>
      </Button>
      {renderChat()}
    </div>
  );
}

// Enhanced message view for non-fullHeight mode
function EnhancedMessageView({
  thread,
  visibleMessages,
  threadMap,
  onSendMessage,
  onAttachment,
  onDownload,
  onSendDraft,
  onDeleteDraft,
  institutionName,
  fullHeight: _fullHeight = false,
  editingDraftId,
  getEditingDraftContent,
  handleUpdateDraft,
  setEditingDraftId,
  handleSaveDraft,
  onEditDraft,
  messageSubject,
  setMessageSubject
}: {
  thread: ChatThread;
  visibleMessages: Array<{ message: ChatMessage; thread: ChatThread }>;
  threadMap: Map<string, ChatThread>;
  onSendMessage: (content: string) => void;
  onAttachment?: () => void;
  onDownload?: () => void;
  onSendDraft?: (messageId: string) => void;
  onDeleteDraft?: (messageId: string) => void;
  institutionName: string;
  fullHeight?: boolean;
  editingDraftId?: string | null;
  getEditingDraftContent: () => string;
  handleUpdateDraft: (content: string) => void;
  setEditingDraftId: (id: string | null) => void;
  handleSaveDraft: (content: string, draftId?: string) => string;
  onEditDraft?: (messageId: string) => void;
  messageSubject: string;
  setMessageSubject: (subject: string) => void;
}) {
  // Non-fullHeight layout with proper flex structure
  return (
    <>
      {/* Header */}
      <div className="shrink-0">
        <ChatHeader
          institutionName={institutionName}
          unreadCount={thread.unreadCount}
          onDownload={onDownload}
        />
      </div>

      {/* Messages - Scrollable area */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <MessageScrollArea
          thread={thread}
          visibleMessages={visibleMessages}
          threadMap={threadMap}
          onSendDraft={onSendDraft}
          onDeleteDraft={onDeleteDraft}
          onEditDraft={onEditDraft}
          editingDraftId={editingDraftId}
        />
      </div>

      {/* Input with Subject - Always at bottom */}
      <div className="mt-auto shrink-0">
        <ChatInput
          onSend={onSendMessage}
          onSaveDraft={handleSaveDraft}
          onAttach={onAttachment}
          placeholder={`Message ${institutionName}...`}
          editingDraftContent={getEditingDraftContent()}
          onUpdateDraft={handleUpdateDraft}
          onCancelEdit={() => setEditingDraftId(null)}
          subject={messageSubject}
          onSubjectChange={setMessageSubject}
        />
      </div>
    </>
  );
}
